import React, { JSX, useEffect, useRef, useState } from 'react';
import {
  FaceLandmarksDetector,
  Keypoint,
  SupportedModels,
  createDetector,
} from '@tensorflow-models/face-landmarks-detection';
import '@tensorflow/tfjs-backend-webgl';
// eslint-disable-next-line import/no-extraneous-dependencies
import * as tf from '@tensorflow/tfjs-core';
import { BiometricAuthRequest } from '@/model/BiometricAuthRequest';
import { BiometricAuthResponse } from '@/model/BiometricAuthResponse';
import { api, AxiosError } from '@/services/biometricApi';
import { SearchFaceByImageRequest } from '@/model/SearchFaceByImageRequest';
import { SearchFaceByImageResponse } from '@/model/SearchFaceByImageResponse';
import { CustomError } from '@/model/CustomError';
import { PostMessageCheckRequest } from '@/model/PostMessageCheckRequest';
import { BiometricCheckErrorResponse } from '@/model/BiometricCheckErrorResponse';
import detectMobile from '@/utils/detectMobile';
import { CheckContainer } from './ui';
import { CameraSize, Dimension, DistanceStatus, EvaluateFace } from '../types/index';

interface StateProps {
  request: PostMessageCheckRequest;
}

interface DispatchProps {
  onAnalysisComplete: (data: SearchFaceByImageResponse[]) => void;
  onAnalysisError: (data: BiometricCheckErrorResponse) => void;
}

type Props = StateProps & DispatchProps;

export const CheckScreen: React.FC<Props> = ({
  request,
  onAnalysisComplete,
  onAnalysisError,
}): JSX.Element => {
  const [message, setMessage] = useState<string>();
  const [biometricToken, setBiometricToken] = useState<string>();
  const [cameraSize, setCameraSize] = useState<CameraSize>();
  const [detector, setDetector] = useState<FaceLandmarksDetector>();
  const [checkedImage, setCheckedImage] = useState<boolean>(false);
  const [imageBase64, setImageBase64] = useState<string>();
  const [loading, setLoading] = useState<boolean>(false);

  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const emaRef = useRef({ relativeFaceHeight: null as null | number, framesStable: 0 });
  const calibrationRef = useRef({ calibratedValue: null as number | null, calibrated: false });

  const LEFT_EYE_IDX = [33, 133];
  const RIGHT_EYE_IDX = [362, 263];

  const handleonAuthBiometric = async (): Promise<void> => {
    setMessage('Autenticando...   ');
    const payload: BiometricAuthRequest = {
      company: request.company,
      origin: request.origin,
      systemOS: request.systemOS,
      browser: request.browser as string,
    };
    const headers = {
      'Content-Type': 'application/json',
      'Api-Key': request.apiKey,
    };
    const { data } = await api.post<BiometricAuthResponse>('/auth/v1', payload, {
      headers,
    });
    setBiometricToken(data.token);
  };

  const handleOnLoadDimessions = (): void => {
    setMessage('Obtendo dimensões da tela...   ');
    const windowInnerWidth = window.innerWidth;
    const windowInnerHeight = window.innerHeight;
    setCameraSize({ width: windowInnerWidth, height: windowInnerHeight });
    /*
    const el = document.getElementById('root') as HTMLDivElement;
    if (el) {
      setCameraSize({ width: el.offsetWidth, height: el.offsetHeight });
    }
      */
  };

  const distance = (a: Keypoint, b: Keypoint): number => Math.hypot(a.x - b.x, a.y - b.y);

  const avgPoint = (pts: Keypoint[]): Keypoint => {
    const s = pts.reduce((acc, p) => ({ x: acc.x + p.x, y: acc.y + p.y }), { x: 0, y: 0 });
    return { x: s.x / pts.length, y: s.y / pts.length };
  };

  const toPixels = (pt: Keypoint, canvas: HTMLCanvasElement): Dimension => {
    if (pt.x <= 1 && pt.y <= 1) {
      return { x: pt.x * canvas.width, y: pt.y * canvas.height };
    }
    return { x: pt.x, y: pt.y };
  };

  const evaluateFace = (faceKeypoints: Keypoint[], canvas: HTMLCanvasElement): EvaluateFace => {
    const leftEyePts = LEFT_EYE_IDX.map(i => toPixels(faceKeypoints[i], canvas));
    const rightEyePts = RIGHT_EYE_IDX.map(i => toPixels(faceKeypoints[i], canvas));
    const eyeLeftCenter = avgPoint(leftEyePts);
    const eyeRightCenter = avgPoint(rightEyePts);

    const topPoint = toPixels(faceKeypoints[10], canvas);
    const chinPoint = toPixels(faceKeypoints[152], canvas);

    const interEye = distance(eyeLeftCenter, eyeRightCenter);
    const faceHeight = distance(topPoint, chinPoint);

    const relativeFaceHeight = faceHeight / canvas.height;

    const alpha = 0.2;
    const prev = emaRef.current.relativeFaceHeight;
    const smoothed =
      prev === null ? relativeFaceHeight : prev * (1 - alpha) + relativeFaceHeight * alpha;
    emaRef.current.relativeFaceHeight = smoothed;

    const cx = toPixels(faceKeypoints[168], canvas).x;
    const cy = toPixels(faceKeypoints[168], canvas).y;
    const centeredX = Math.abs(cx - canvas.width / 2) / canvas.width < 0.15;
    const centeredY = Math.abs(cy - canvas.height / 2) / canvas.height < 0.15;

    const angle =
      Math.atan2(eyeRightCenter.y - eyeLeftCenter.y, eyeRightCenter.x - eyeLeftCenter.x) *
      (180 / Math.PI);
    const headStraight = Math.abs(angle) < 8;

    if (centeredX && centeredY && headStraight) {
      emaRef.current.framesStable = (emaRef.current.framesStable || 0) + 1;
    } else {
      emaRef.current.framesStable = 0;
    }

    if (!calibrationRef.current.calibrated && emaRef.current.framesStable >= 10) {
      calibrationRef.current.calibratedValue = smoothed;
      calibrationRef.current.calibrated = true;
    }

    let nearThreshold: number;
    let farThreshold: number;
    if (calibrationRef.current.calibrated && calibrationRef.current.calibratedValue != null) {
      const base = calibrationRef.current.calibratedValue;
      nearThreshold = base * 1.2;
      farThreshold = base * 0.8;
    } else {
      nearThreshold = 0.45;
      farThreshold = 0.23;
    }

    let distanceStatus: DistanceStatus = DistanceStatus.OK;
    if (smoothed > nearThreshold) distanceStatus = DistanceStatus.NEAR;
    else if (smoothed < farThreshold) distanceStatus = DistanceStatus.FAR;

    const ratio = interEye / faceHeight;

    return {
      centered: centeredX && centeredY,
      distanceStatus,
      headStraight,
      smoothedRelativeFaceHeight: smoothed,
      ratio,
      interEye,
      faceHeight,
      angle,
    };
  };

  const loadDetector = async (): Promise<void> => {
    setMessage('Carregando modelos...   ');
    await tf.setBackend('webgl');
    const det = await createDetector(SupportedModels.MediaPipeFaceMesh, {
      runtime: 'mediapipe',
      solutionPath: 'https://cdn.jsdelivr.net/npm/@mediapipe/face_mesh',
      refineLandmarks: true,
      maxFaces: 1,
    });

    setDetector(det);
  };

  const handleOnSetupCamera = async (): Promise<void> => {
    setMessage('Configurando a câmera...   ');
    if (!videoRef.current || !cameraSize) return;

    // const { width, height } = cameraSize;

    const stream = await navigator.mediaDevices.getUserMedia({
      video: { facingMode: 'user' },
      audio: false,
    });

    videoRef.current.srcObject = stream;
    if (videoRef !== null) {
      videoRef.current.style.cssText =
        '-moz-transform: scale(-1, 1); -webkit-transform: scale(-1, 1); -o-transform: scale(-1, 1); transform: scale(-1, 1); filter: FlipH;';
    }
    await new Promise(res => {
      videoRef.current!.onloadedmetadata = () => res(null);
    });

    videoRef.current.play();
    if (!detector) {
      await loadDetector();
    } else {
      setTimeout(() => {
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        startDetectionLoop();
      }, 3000);
    }
  };

  const handleOnValidateImage = async (image: string): Promise<void> => {
    setCheckedImage(true);

    setLoading(true);
    setMessage('Verificando Imagem...');
    try {
      const payload: SearchFaceByImageRequest = {
        imageBase64: image,
      };
      const { data } = await api.post<SearchFaceByImageResponse[]>(
        '/rekognition/v1/search-face',
        payload,
        {
          headers: {
            Authorization: `Bearer ${biometricToken}`,
          },
        },
      );
      onAnalysisComplete(data);
    } catch (error) {
      let messageError = '';
      let status = 500;
      if (error instanceof AxiosError) {
        const axiosError = error as AxiosError<CustomError, CustomError>;
        status = axiosError.status || 500;
        if (axiosError.status === 401 && request) {
          await handleonAuthBiometric();
        } else if (axiosError.response && axiosError.response.data) {
          if (axiosError.response.data.details) {
            // eslint-disable-next-line no-plusplus
            for (let i = 0; i < axiosError.response.data.details.length; i++) {
              if (i > 0) {
                break;
              }
              messageError = axiosError.response.data.details[i];
            }
          } else if (axiosError.response.data.message) {
            messageError = axiosError.response.data.message;
          }
        }
      } else {
        messageError = 'Erro ao verificar imagem!';
      }

      const errorResponse = {
        success: false,
        message: messageError,
        status,
      };
      onAnalysisError(errorResponse);
    } finally {
      setLoading(false);
      setTimeout(() => {
        setCheckedImage(false);
      }, 2000);

      setImageBase64(undefined);
      setMessage(undefined);
      if (request && request.keepChecking) {
        setTimeout(() => {
          if (videoRef.current) {
            handleOnSetupCamera();
          }
        }, 300);
      }
    }
  };

  const handleOnTakePicture = async (): Promise<void> => {
    if (!videoRef.current) return;

    const video = videoRef.current;
    const targetAspect = 3 / 4;

    const { videoWidth } = video;
    const { videoHeight } = video;
    const videoAspect = videoWidth / videoHeight;

    let sx = 0;
    let sy = 0;
    let width = videoWidth;
    let height = videoHeight;

    if (videoAspect > targetAspect) {
      height = videoHeight;
      width = videoHeight * targetAspect;
      sx = (videoWidth - width) / 2;
    } else {
      width = videoWidth;
      height = videoWidth / targetAspect;
      sx = 0;
      sy = (videoHeight - height) / 2;
    }

    const outputWidth = 600;
    const outputHeight = Math.round(outputWidth / targetAspect);

    const canvas = document.createElement('canvas');
    canvas.width = outputWidth;
    canvas.height = outputHeight;

    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.drawImage(video, sx, sy, width, height, 0, 0, canvas.width, canvas.height);

      const image = canvas.toDataURL('image/jpeg', 0.9);
      setImageBase64(image);
    }
    // if (cameraSize && videoRef && videoRef.current) {
    //   const canvas = document.createElement('canvas');
    //   canvas.width = cameraSize.width;
    //   canvas.height = cameraSize.height;
    //   const ctx = canvas.getContext('2d');
    //   if (ctx) {
    //     ctx.drawImage(videoRef.current, 0, 0, canvas.width, canvas.height);
    //     const image = canvas.toDataURL('image/jpeg');
    //     setImageBase64(image);
    //   }
    // }
  };

  /** Loop de detecção com avaliação completa */
  const startDetectionLoop = (): void => {
    setMessage('Inciando detecção de face...');
    if (!detector || !videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d')!;

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    const detect = async (): Promise<void> => {
      const faces = await detector.estimateFaces(video);

      ctx.clearRect(0, 0, canvas.width, canvas.height);

      if (faces.length === 1) {
        const face = faces[0];
        const landmarks = face.keypoints;

        const result = evaluateFace(landmarks, canvas);

        /** Mensagem */
        let localMessage = 'Tudo OK!';

        if (!result.centered) {
          localMessage = 'Centralize o rosto';
        } else if (result.distanceStatus === DistanceStatus.NEAR) {
          localMessage = 'Afaste um pouco o rosto';
        } else if (result.distanceStatus === DistanceStatus.FAR) {
          localMessage = 'Aproxime um pouco o rosto';
        } else if (!result.headStraight) {
          localMessage = 'Mantenha a cabeça reta';
        } else if (
          result.centered &&
          result.distanceStatus === DistanceStatus.OK &&
          result.headStraight
        ) {
          localMessage = 'Capturando imagem...';
          handleOnTakePicture();
          video.pause();
        }

        setMessage(localMessage);

        ctx.strokeStyle = 'lime';
        ctx.lineWidth = 3;
        ctx.strokeRect(
          faces[0].box.xMin - 60,
          faces[0].box.yMin,
          faces[0].box.width + 70,
          faces[0].box.height,
        );
      }

      requestAnimationFrame(detect);
    };

    detect();
  };

  useEffect(() => {
    if (request) {
      handleonAuthBiometric();
    }
  }, [request]);

  useEffect(() => {
    if (biometricToken) {
      handleOnLoadDimessions();
    }
  }, [biometricToken]);

  useEffect(() => {
    if (cameraSize) {
      handleOnSetupCamera();
    }
  }, [cameraSize]);

  useEffect(() => {
    if (detector) {
      startDetectionLoop();
    }
  }, [detector]);

  useEffect(() => {
    if (!checkedImage && imageBase64) {
      handleOnValidateImage(imageBase64);
    }
  }, [imageBase64]);

  return (
    <CheckContainer
      loading={loading}
      message={message}
      cameraSize={cameraSize}
      videoRef={videoRef}
      canvasRef={canvasRef}
      imageBase64={imageBase64}
      isMobile={detectMobile()}
    />
  );
};
